import React from 'react';
import { cn } from '@/lib/utils';
import { getEventCategory, getEventTypeIconUrl, EventCategory } from '@/lib/event-type-icons';

// Event type labels
export const EVENT_TYPE_LABELS: Record<string, string> = {
  price_increased: 'Price Increased',
  price_decreased: 'Price Decreased',
  price_hidden: 'Price Hidden',
  price_revealed: 'Price Revealed',
  plan_added: 'Plan Added',
  plan_removed: 'Plan Removed',
  plan_renamed: 'Plan Renamed',
  discount_added: 'Discount Added',
  discount_removed: 'Discount Removed',
  discount_changed: 'Discount Changed',
  capacity_increased: 'Capacity Increased',
  capacity_decreased: 'Capacity Decreased',
  threshold_added: 'Threshold Added',
  threshold_removed: 'Threshold Removed',
  feature_added: 'Feature Added',
  feature_removed: 'Feature Removed',
  feature_changed: 'Feature Changed',
  addon_added: 'Add-on Added',
  addon_removed: 'Add-on Removed',
  addon_changed: 'Add-on Changed',
  trial_changed: 'Trial Changed',
  freemium_changed: 'Freemium Changed',
  pricing_restructured: 'Pricing Restructured',
  pricing_metric_changed: 'Metric Changed',
  positioning_changed: 'Positioning Changed',
};

// Category color configuration - consistent across the app
export const CATEGORY_COLORS: Record<EventCategory, {
  bg: string;
  selectedBg: string;
  border: string;
  accent: string;
  accentHex: string;
  text: string;
  dot: string;
}> = {
  pricing: {
    bg: 'bg-[#1a1f36]/5 dark:bg-[#1a1f36]/20',
    selectedBg: 'bg-[#1a1f36]/10 dark:bg-[#1a1f36]/30',
    border: 'border-[#1a1f36]/20 dark:border-[#1a1f36]/40',
    accent: 'bg-[#1a1f36]',
    accentHex: '#1a1f36',
    text: 'text-[#1a1f36] dark:text-[#8b92b3]',
    dot: 'bg-[#1a1f36]',
  },
  packaging: {
    bg: 'bg-[#2563a8]/5 dark:bg-[#2563a8]/20',
    selectedBg: 'bg-[#2563a8]/10 dark:bg-[#2563a8]/30',
    border: 'border-[#2563a8]/20 dark:border-[#2563a8]/40',
    accent: 'bg-[#2563a8]',
    accentHex: '#2563a8',
    text: 'text-[#2563a8] dark:text-[#6b9fd8]',
    dot: 'bg-[#2563a8]',
  },
  product: {
    bg: 'bg-[#9ba855]/5 dark:bg-[#9ba855]/20',
    selectedBg: 'bg-[#9ba855]/10 dark:bg-[#9ba855]/30',
    border: 'border-[#9ba855]/20 dark:border-[#9ba855]/40',
    accent: 'bg-[#9ba855]',
    accentHex: '#9ba855',
    text: 'text-[#7a8544] dark:text-[#b8c476]',
    dot: 'bg-[#9ba855]',
  },
  other: {
    bg: 'bg-gray-50 dark:bg-gray-900/30',
    selectedBg: 'bg-gray-100 dark:bg-gray-800/40',
    border: 'border-gray-200 dark:border-gray-700',
    accent: 'bg-gray-500',
    accentHex: '#6b7280',
    text: 'text-gray-700 dark:text-gray-300',
    dot: 'bg-gray-500',
  },
};

// Event Type Icon component - uses Cloudinary icons
interface EventTypeIconProps {
  eventType: string;
  className?: string;
}

export function EventTypeIcon({ eventType, className = "w-5 h-5" }: EventTypeIconProps) {
  const category = getEventCategory(eventType);
  const categoryFolder = category === 'pricing' ? 'pricing_events' :
    category === 'packaging' ? 'packaging_events' :
    category === 'product' ? 'product_events' : 'pricing_events';

  const iconUrl = getEventTypeIconUrl(eventType) ??
    `https://res.cloudinary.com/dd6dkaan9/image/upload/v1770090635/pricing-icons/${categoryFolder}/${eventType}.png`;

  return (
    <img
      src={iconUrl}
      alt={EVENT_TYPE_LABELS[eventType] || eventType}
      className={cn("object-contain", className)}
      onError={(e) => {
        // Hide on error
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  );
}

// Compact event type badge with icon and label
interface EventTypeBadgeProps {
  eventType: string;
  showLabel?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

export function EventTypeBadge({
  eventType,
  showLabel = true,
  size = 'sm',
  className
}: EventTypeBadgeProps) {
  const category = getEventCategory(eventType);
  const colors = CATEGORY_COLORS[category];
  const label = EVENT_TYPE_LABELS[eventType] || eventType.replace(/_/g, ' ');

  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';
  const padding = size === 'sm' ? 'px-2 py-0.5' : 'px-2.5 py-1';

  return (
    <span className={cn(
      "inline-flex items-center gap-1.5 rounded border",
      padding,
      colors.bg,
      colors.border,
      className
    )}>
      <EventTypeIcon eventType={eventType} className={iconSize} />
      {showLabel && (
        <span className={cn(textSize, colors.text, "font-medium whitespace-nowrap")}>
          {label}
        </span>
      )}
    </span>
  );
}

// Category indicator with dot and label
interface CategoryIndicatorProps {
  category: EventCategory;
  count?: number;
  showLabel?: boolean;
  className?: string;
}

export function CategoryIndicator({
  category,
  count,
  showLabel = true,
  className
}: CategoryIndicatorProps) {
  const colors = CATEGORY_COLORS[category];
  const label = category === 'pricing' ? 'Pricing' :
    category === 'packaging' ? 'Packaging' :
    category === 'product' ? 'Product' : 'Other';

  return (
    <span className={cn("inline-flex items-center gap-1.5 text-sm", className)}>
      <span className={cn("w-2 h-2 rounded-full", colors.dot)} />
      {showLabel && (
        <span className={cn("text-muted-foreground", colors.text)}>
          {count !== undefined ? `${count} ${label}` : label}
        </span>
      )}
    </span>
  );
}

// Category separator (dot or line)
interface CategorySeparatorProps {
  type?: 'dot' | 'line';
  category?: EventCategory;
  className?: string;
}

export function CategorySeparator({
  type = 'dot',
  category,
  className
}: CategorySeparatorProps) {
  const colors = category ? CATEGORY_COLORS[category] : null;

  if (type === 'dot') {
    return (
      <span className={cn(
        "w-1 h-1 rounded-full shrink-0",
        colors ? colors.dot : 'bg-muted-foreground/40',
        className
      )} />
    );
  }

  return (
    <span className={cn(
      "flex-1 h-px",
      colors ? cn(colors.accent, "opacity-30") : 'bg-muted-foreground/20',
      className
    )} />
  );
}

// Get category colors helper
export function getCategoryColors(eventType: string) {
  const category = getEventCategory(eventType);
  return CATEGORY_COLORS[category];
}
